import type { FC } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FileDropzone } from "src/components/file-dropzone";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import toast from "react-hot-toast";

import LoadingButton from "@mui/lab/LoadingButton";
import { MinimalEstateDocument } from "src/types/snugdocs";
import React from "react";

interface UploadSignedCopyDialogProps {
  open: boolean;
  mostRecentEstateDoc: MinimalEstateDocument | null;
  documentId?: string | null;
  personUdId?: string | null;
  estateDocumentSaving: boolean;
  estateDocFilesToUpload: File[];
  handleEstateDocumentFilesDrop: (files: File[], isSignedCopy: boolean) => void;
  handleEstateDocumentFileRemove: (file: File) => void;
  handleEstateDocumentFilesRemoveAll: () => void;
  onCancel: () => void;
  handleEdiEstateDocumentPatchOnly: (
    estateDocument: Partial<MinimalEstateDocument>
  ) => void;
  // Supports both old signature (documentId, file) and new signature (personUdId, documentId, file)
  handleUploadSignedCopy?: ((...args: any[]) => Promise<any>);
  onUploadSuccess?: (documentId: string) => void;
  refreshHouseholds: () => void;
}

export const UploadSignedCopyDialog: FC<UploadSignedCopyDialogProps> = (
  props
) => {
  const {
    open,
    onCancel,
    documentId,
    personUdId,
    estateDocFilesToUpload,
    estateDocumentSaving,
    handleEstateDocumentFilesDrop,
    handleEstateDocumentFileRemove,
    handleEstateDocumentFilesRemoveAll,
    handleUploadSignedCopy,
    handleEdiEstateDocumentPatchOnly,
    refreshHouseholds,
    onUploadSuccess,
  } = props;

  const [success, setSuccess] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);

  const handleUpload = async () => {
    if (estateDocFilesToUpload.length === 0) {
      toast.error("Please select a file to upload");
      return;
    }

    try {
      setUploading(true);

      // New flow: Use DocumentV2 upload handler if available
      if (handleUploadSignedCopy && documentId) {
        // Upload each file (though typically there should only be 1)
        for (const file of estateDocFilesToUpload) {
          // If personUdId is provided, use new signature: (personUdId, documentId, file)
          // Otherwise use old signature: (documentId, file) where ud_id is baked into the function
          if (personUdId) {
            await (handleUploadSignedCopy as any)(personUdId, documentId, file);
          } else {
            await (handleUploadSignedCopy as any)(documentId, file);
          }
        }



        setSuccess(true);


        // Notify parent of successful upload
        if (onUploadSuccess ) {
          refreshHouseholds();
          onUploadSuccess(documentId);
        }

        // Clear files and close dialog after short delay
        setTimeout(() => {
          handleEstateDocumentFilesRemoveAll();
          setSuccess(false);
          setUploading(false);
          onCancel();
        }, 1500);
      }
      // Old flow: Use estate document patch for legacy flows
      else {
        await handleEdiEstateDocumentPatchOnly({
          id: props.mostRecentEstateDoc?.id,
        });
        setTimeout(() => {
          window.location.reload(); // Reload the page to pull the latest data
        }, 1000);
        setSuccess(true);
      }
    } catch (error) {
      console.error(error);
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleEstateDocumentFilesRemoveAll();
        setSuccess(false);
        onCancel();
      }}
      fullWidth
      maxWidth={"sm"}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box sx={{ flexGrow: 1, p: 4 }}>
          <Stack spacing={6}>
            <Stack spacing={3}>
              <Typography variant="h4">Upload signed copy</Typography>
              <Typography variant="body2">
                Upload your signed copy as {handleUploadSignedCopy ? "a" : "one or more"} scanned *.pdf file{handleUploadSignedCopy ? "" : "s"}. All
                documents are stored securely and shared only with the trusted
                individuals you choose.
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#FAF9F8",
                  borderRadius: 2,
                  mt: 3,
                  p: 2,
                  mx: 4,
                  border: "1px solid #F2F4F7",
                }}
              >
                <FileDropzone
                  maxFiles={handleUploadSignedCopy ? 1 : 10}
                  minSize={1}
                  maxSize={100000000}
                  useFsAccessApi={false}
                  caption={handleUploadSignedCopy ? "(pdf, zip, doc, txt, jpg, png, up to 100MB)" : "(pdf, zip, doc, txt, jpg, png, up to 100MB, max 10 files)"}
                  files={estateDocFilesToUpload}
                  onDrop={(files) => handleEstateDocumentFilesDrop(files, true)}
                  onRemove={handleEstateDocumentFileRemove}
                  onRemoveAll={handleEstateDocumentFilesRemoveAll}
                  uploading={uploading || estateDocumentSaving}
                  disabled={uploading || estateDocumentSaving}
                  blockUploadButton={true}
                />
              </Box>

              <Stack
                alignItems="center"
                direction="row"
                flexWrap="wrap"
                spacing={2}
                justifyContent="space-between"
              >
                <Stack
                  alignItems="center"
                  direction="row"
                  flexWrap="wrap"
                  spacing={1}
                >
                  <LoadingButton
                    color={success ? "success" : "primary"}
                    type="submit"
                    variant="contained"
                    loading={uploading}
                    startIcon={<FileUploadOutlinedIcon />}
                    onClick={handleUpload}
                  >
                    {success ? "Success!" : "Upload file"}
                  </LoadingButton>
                  <Button
                    color="inherit"
                    disabled={uploading}
                    onClick={onCancel}
                    size="small"
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
};
